import { ApolloCache } from "@apollo/client"
import { stripTypename } from "@apollo/client/utilities"

import { MutationTypename } from "shared/api/types"
import { getObjectDifference } from "shared/lib/objectUtils"

import { didMove } from "entities/project-children/lib/didMove"
import { getChildPositionData } from "entities/project-children/lib/getChildMoveData"

import { getRoomComplete } from "../lib/getRoomComplete"
import { getRoomNumberFallback } from "../lib/getRoomNumberFallback"
import { readRoomCompleteFragment } from "../lib/readRoomFragment"
import { RoomUpdate } from "../types/Room"

export function prepareRoomUpdate({
  room,
  displayPosition,
  cache,
  projectID,
  parentID,
  childType = "Room"
}: {
  room: RoomUpdate
  displayPosition?: number
  cache: ApolloCache<object>
  projectID: string
  parentID?: string | null
  childType?: "Room"
}) {
  const cachedData = readRoomCompleteFragment(room.id, cache)

  if (!cachedData) {
    throw new Error(`Unable to read cached data for Room: ${room.id}`)
  }

  const { children, ...roomWithoutChildren } = cachedData
  const { children: updatedChildren, ...roomUpdateWithoutChildren } = room

  const changedValues = getObjectDifference(
    roomUpdateWithoutChildren,
    roomWithoutChildren || {}
  )

  const moveData = getChildPositionData({
    projectID,
    parentID,
    childID: room.id,
    childType,
    displayPosition,
    cache
  })

  const number = getRoomNumberFallback(
    { ...cachedData, ...changedValues },
    "update"
  )

  const updatedRoom = {
    ...cachedData,
    ...changedValues,
    number,
    position: moveData.nextPosition
  }

  const optimisticRoom = getRoomComplete(updatedRoom)

  return {
    changedValues,
    input: stripTypename({
      id: room.id,
      ...changedValues,
      ...(cachedData.number || changedValues.number ? {} : { number }),
      position: didMove(moveData) ? moveData.nextPosition : undefined,
      parent:
        didMove(moveData) && moveData.prevParentID !== moveData.nextParentID
          ? {
              id: moveData.nextParentID,
              type: moveData.nextParentType
            }
          : undefined
    }),
    optimisticResponse: optimisticRoom
      ? { __typename: MutationTypename, room: optimisticRoom }
      : undefined,
    children,
    moveData
  }
}
